import React, { useEffect, useRef } from 'react'

import { css, Global, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import FocusLock from 'react-focus-lock'

import { breakpoints, spacing, usePreventScroll } from '@cash-design-system/react'
import logger from '@cash-web/shared/util-logger'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import useMediaQuery from '@cash-web/shared-ui-use-media-query'
import { WebViewViewComponent } from '@squareup/customer-data-events-web'

import { Modal as ArcadeModalWrapper } from './Modal/Modal'
import { ModalActionProvider } from './ModalActionProvider'
import { ModalActions, ModalVariant } from './ModalActions'
import { ModalContextProvider } from './ModalContext'
import { ModalPane } from './ModalPane'

type DialogProps = {
  hideOverflow?: boolean
  disableAnimation?: boolean
  width?: string
  isHelpAction?: boolean
  closable?: boolean
  fixed?: boolean
  variant?: ModalVariant
}

export const Position = styled.div`
  z-index: 99999;
  position: fixed;
  inset: 0 0 0 0;
`

export const DialogScrollableBackground = styled.div<DialogProps>`
  ${breakpoints.tablet} {
    ${props =>
      !props.disableAnimation &&
      css`
        animation: ${keyframes({
            '0%': { background: 'rgba(0, 0, 0, 0)' },
            '100%': {
              background: props.theme.arcadeColors.semantic.backgroundDimmer,
              backdropFilter: 'blur(.06rem)',
            },
          })}
          200ms forwards;
      `}
    ${props =>
      props.isHelpAction &&
      css`
        border-radius: 1.5rem;
      `}
    background: ${props => props.theme.arcadeColors.semantic.backgroundDimmer};
    padding: ${spacing.defaultContent};
    /* stylelint-disable-next-line number-max-precision */
    filter: opacity(0.99999);
    position: fixed;
    overflow-y: auto;
    inset: 0 0 0 0;
    display: flex;
    justify-content: center;
    align-items: center;
    ${({ fixed }) =>
      fixed &&
      css`
        align-items: start;
      `}
    ${({ variant, theme: { spacing } }) =>
      variant !== 'receipt' &&
      css`
        padding: ${spacing.xxxl} ${spacing.empty};
      `}
  }
`

const popUp = keyframes`
    0% {
        transform: translateY(1rem);
    }
    100% {
        transform: translateY(0);
    }
`

export const Dialog = styled.div<DialogProps>`
  position: relative;
  background: ${props => props.theme.arcadeColors.component.modalBackground};
  min-height: 100vh;
  max-height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  ${({ variant, theme: { spacing } }) =>
    variant !== 'receipt' &&
    css`
      padding: ${spacing.xl} ${spacing.xl} ${spacing.headerSpacing} ${spacing.xl};
    `}
  display: flex;
  flex-direction: column;

  &:focus-visible {
    outline: none;
  }

  ${breakpoints.tablet} {
    max-height: fit-content;
    border-radius: 1.5rem;

    ${({ variant, theme: { spacing } }) =>
      variant !== 'receipt' &&
      css`
        padding: ${spacing.xl};
      `}

    ${props =>
      !props.disableAnimation &&
      css`
        animation: ${popUp} 200ms forwards;
      `}
    width: ${props => props.width};
    box-shadow: 0 12px 24px 0 #0000000a;
    ${({ variant, closable }) =>
      variant !== 'receipt' &&
      css`
        padding: ${closable === false ? '3.5rem 2.5rem' : '2.5rem 2.5rem 3.5rem 2.5rem'};
      `}
    min-height: unset;

    ${({ variant }) =>
      variant === 'default' &&
      `
        overflow: visible;
      `}
    ${({ fixed }) =>
      !fixed &&
      css`
        margin: auto 0;
      `}
      ${({ hideOverflow }) =>
      hideOverflow &&
      css`
        overflow: hidden;
      `}
  }
  ${({ variant, theme: { spacing } }) =>
    variant === 'receipt' &&
    css`
      overflow: hidden;
      ${breakpoints.tablet} {
        padding: ${spacing.empty};
      }
    `}
`

const ModalContent = styled.div<{ overflowScroll: boolean; variant: ModalVariant }>`
  /* On small screens we want the modal content to scroll, not the entire dialog container */
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  flex: 1 1 auto;
  -webkit-overflow-scrolling: touch;

  /* ensure content is not hidden by os specific elements on mobile */
  padding-bottom: ${spacing.l};

  ${breakpoints.tablet} {
    padding-bottom: 0;
    overflow-x: unset;
    overflow-y: ${({ overflowScroll }) => (overflowScroll ? 'auto' : 'visible')};
  }
`

const OverflowScrollFocusLock = styled(FocusLock)`
  overflow-y: auto;
`

export interface ModalProps extends DialogProps {
  onClose?: () => void
  onBack?: () => void
  closable?: boolean
  children?: React.ReactNode
  overflowScroll?: boolean
  dataTestId?: string
}

export const BodyOverFlowHidden = css`
  body {
    overflow: hidden;
  }
`

export const useModal = ({ onClose }: { onClose?: () => void }) => {
  const closeOnEscape: React.KeyboardEventHandler<HTMLDivElement> = event => {
    if (event.key === 'Escape') {
      onClose?.()
    }
  }

  return { closeOnEscape }
}

const WIDTHS: Record<ModalVariant, string | undefined> = {
  default: '31rem',
  receipt: '23rem',
  fullscreen: undefined,
}

const LegacyModal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      children,
      onClose,
      onBack,
      closable = true,
      disableAnimation = false,
      hideOverflow = false,
      variant = 'default',
      width = WIDTHS[variant],
      overflowScroll = false,
      isHelpAction,
      fixed = false,
      dataTestId,
    }: ModalProps,
    ref
  ) => {
    const isDesktop = useMediaQuery(breakpoints.tablet)
    const { closeOnEscape } = useModal({ onClose })
    const logCdfEvent = useLogCashWebCdfEvent()

    // On mobile, prevent body scroll and maintain scroll position while modal is open.
    // Fixes iOS 26 bug where body remains scrollable under the modal.
    // Can be removed if the bug is fixed in a future iOS version.
    usePreventScroll({ prevent: !isDesktop })

    const hasLoggedShowModalRef = useRef(false)
    useEffect(() => {
      if (!hasLoggedShowModalRef.current) {
        logCdfEvent(WebViewViewComponent({ componentName: 'Modal', componentType: 'Open' }))
        hasLoggedShowModalRef.current = true
      }
    }, [logCdfEvent])

    // If variant is fullscreen, render ModalPane
    if (variant === 'fullscreen') {
      return (
        <ModalPane ref={ref} onBack={onBack} onClose={onClose} dataTestId={dataTestId ?? 'fullscreen-modal'}>
          {children}
        </ModalPane>
      )
    }

    return (
      <>
        <Global styles={BodyOverFlowHidden} />
        <Position>
          <DialogScrollableBackground
            data-testid={'modal-background'}
            onClick={() => {
              if (closable) {
                logger.addBreadcrumb('Close modal')
                onClose?.()
              }
            }}
            disableAnimation={disableAnimation}
            isHelpAction={isHelpAction}
            fixed={fixed}
            variant={variant}
            ref={ref}
          >
            {/* we don't want any clicks on the dialog to propagate and force the window closed */}
            <OverflowScrollFocusLock
              as={Dialog}
              lockProps={{
                onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation(),
                onKeyDown: closeOnEscape,
                role: 'dialog',
                'data-testid': dataTestId,
                disableAnimation: disableAnimation,
                width: width,
                fixed: fixed,
                ariaModal: true,
                closable,
                variant,
                hideOverflow,
              }}
            >
              <ModalContextProvider>
                <ModalActionProvider>
                  {closable ? <ModalActions onClose={onClose} onBack={onBack} variant={variant} /> : null}
                  <ModalContent overflowScroll={overflowScroll} variant={variant} data-testid="modal-content">
                    {children}
                  </ModalContent>
                </ModalActionProvider>
              </ModalContextProvider>
            </OverflowScrollFocusLock>
          </DialogScrollableBackground>
        </Position>
      </>
    )
  }
)

/**
 * @deprecated Use ModalPortal
 */
export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      children,
      onClose,
      onBack,
      closable = true,
      disableAnimation = false,
      hideOverflow = false,
      variant = 'default',
      width = WIDTHS[variant],
      overflowScroll = false,
      isHelpAction,
      fixed = false,
      dataTestId,
    }: ModalProps,
    ref
  ) => {
    const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('modal')

    if (isArcadeMigrationEnabled) {
      return (
        <ArcadeModalWrapper
          variant={variant}
          closeButtonProps={{ onClose: closable && onClose ? onClose : undefined }}
          backButtonProps={{ onBack: onBack }}
          allowOverflow={!hideOverflow}
          allowContentScroll={overflowScroll}
          enableAnimation={!disableAnimation}
          maxWidth={width}
        >
          {children}
        </ArcadeModalWrapper>
      )
    }

    return (
      <LegacyModal
        onClose={onClose}
        onBack={onBack}
        closable={closable}
        disableAnimation={disableAnimation}
        hideOverflow={hideOverflow}
        variant={variant}
        width={width}
        overflowScroll={overflowScroll}
        isHelpAction={isHelpAction}
        fixed={fixed}
        dataTestId={dataTestId}
        ref={ref}
      >
        {children}
      </LegacyModal>
    )
  }
)
